
import React from 'react';
import { motion } from 'framer-motion';
import { TECH_CATEGORIES } from '../constants';
import { Zap, ShieldCheck, Cpu, Code2 } from 'lucide-react';

const Stack: React.FC = () => {
  const codeSnippet = `
// System Core Initialization
function initializeSystem(developer) {
  return developer.mastery.map(skill => ({
    ...skill,
    status: 'READY',
    performance: 'OPTIMAL'
  }));
}

const currentDev = {
  name: 'Juanma Tena',
  focus: 'SEO Excellence'
};`;

  return (
    <section id="stack" className="py-24 md:py-40 relative overflow-hidden bg-[#0d0d0d]">
      {/* Background decoration: Code Matrix */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none select-none overflow-hidden font-mono text-[8px] leading-tight" aria-hidden="true">
        {Array(20).fill(codeSnippet).join('\n')}
      </div>

      {/* Neon Orbs */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#39FF14]/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-[#39FF14]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-20 relative z-10">
        <div className="mb-16 md:mb-24">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-[1px] w-12 bg-[#39FF14]/40" />
            <span className="text-[10px] md:text-xs font-bold tracking-[0.4em] uppercase text-[#39FF14] neon-glow">Habilidades Técnicas</span>
          </div>
          <h2 className="font-syncopate text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight">
            TECNOLOGÍAS DE <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#39FF14] to-emerald-400">ALTO RENDIMIENTO</span>
          </h2>
          <p className="text-gray-500 mt-4 max-w-2xl text-xs md:text-sm uppercase font-bold tracking-widest">Stack Full Stack: JavaScript, TypeScript, React, Next.js y Node.js</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Column: Tech Categories */}
          <div className="lg:col-span-8 space-y-12">
            {TECH_CATEGORIES.map((category, catIdx) => (
              <div 
                key={category.name}
                className="space-y-6"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#39FF14] shadow-[0_0_8px_#39FF14]" />
                  <h3 className="font-syncopate text-xs md:text-sm font-bold tracking-[0.2em] text-gray-300 uppercase">{category.name}</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {category.items.map((tech, techIdx) => (
                    <article key={tech.name} className="glass-panel p-4 md:p-5 border border-white/5 rounded-lg group hover:border-[#39FF14]/30 transition-all">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-white group-hover:text-[#39FF14] transition-colors">
                          {tech.name}
                        </span>
                        <span className="text-[9px] font-mono text-gray-500 font-bold">{tech.level}%</span>
                      </div>
                      {/* Skill Bar Container */}
                      <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          whileInView={{ width: `${tech.level}%` }}
                          transition={{ duration: 1.5, delay: techIdx * 0.1, ease: "easeOut" }}
                          viewport={{ once: true }}
                          className="h-full bg-gradient-to-r from-[#39FF14]/40 to-[#39FF14] relative shadow-[0_0_10px_#39FF14]"
                        >
                          <div className="absolute top-0 right-0 w-1 h-full bg-white shadow-[0_0_5px_#fff]" />
                        </motion.div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Right Column: Philosphy & Interactive Element */}
          <aside className="lg:col-span-4 space-y-8">
            <div 
              className="glass-panel p-8 border border-[#39FF14]/20 rounded-2xl relative overflow-hidden group"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-[#39FF14]" />
              <div className="relative z-10">
                <ShieldCheck className="text-[#39FF14] mb-6" size={32} />
                <h4 className="font-syncopate text-sm font-bold mb-4 tracking-wider uppercase">INGENIERÍA WEB SEO-FIRST</h4>
                <p className="text-gray-400 text-xs md:text-sm leading-relaxed mb-6 uppercase tracking-tight">
                  Mi metodología combina el desarrollo robusto con <span className="text-white">SEO Técnico</span> avanzado. No solo construyo webs rápidas, construyo webs que <span className="text-white">venden</span> y escalan en Google.
                </p>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-[10px] font-bold text-[#39FF14]">
                    <Zap size={14} /> CORE WEB VITALS OPTIMIZADOS
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-[#39FF14]">
                    <Code2 size={14} /> SEMÁNTICA HTML5 PURA
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-[#39FF14]">
                    <Cpu size={14} /> DESARROLLO ULTRA RÁPIDO
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#39FF14]/5 blur-3xl rounded-full group-hover:bg-[#39FF14]/10 transition-all" />
            </div>

            {/* Micro-interactive Badge */}
            <div className="glass-panel p-6 border border-white/5 rounded-xl flex items-center justify-between">
              <div className="space-y-1">
                <span className="block text-[10px] text-gray-500 font-bold uppercase tracking-widest">Audit Score</span>
                <span className="block text-xs font-bold text-[#39FF14] font-mono animate-pulse uppercase tracking-wider">100 Performance & SEO</span>
              </div>
              <div className="w-12 h-12 rounded-full border border-[#39FF14]/20 flex items-center justify-center relative">
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 border-t-2 border-[#39FF14] rounded-full"
                />
                <div className="w-2 h-2 bg-[#39FF14] rounded-full shadow-[0_0_8px_#39FF14]" />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Stack;
